import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Dropdown,
  Form,
  Button,
  Modal,
  ListGroup,
  InputGroup,
  ButtonGroup,
} from "react-bootstrap";
import { FaSearch, FaPlane, FaHotel, FaCar, FaChevronDown, FaChevronRight } from "react-icons/fa";
import { API, graphqlOperation } from "aws-amplify";
import { listCompanies, listComplaints } from "../graphql/queries.ts";
import {
  createSubmissions,
  generateOpenAICompletion,
} from "../graphql/mutations.ts";
import Header from "./Header.tsx";
import { BiCopy, BiEnvelope } from "react-icons/bi";
import { v4 as uuidv4 } from "uuid";
import { useAuth } from "../contexts/AuthContext.tsx";

const generateSnitchId = () => {
  const num = Math.floor(1000 + Math.random() * 9000);
  return `SNITCH-${num}`;
};

const TravelSnitchScreen: React.FC = () => {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [snitchId, setSnitchId] = useState("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [buttonText, setButtonText] = useState("Copy to Clipboard");
  const [letterExpanded, setLetterExpanded] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showWin, setShowWin] = useState(false);
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
  const [customItems, setCustomItems] = useState<Set<string>>(new Set());
  const [showModal, setShowModal] = useState<boolean>(false);
  const [category, setCategory] = useState<string>("airline");
  const [checkboxes, setCheckboxes] = useState<string[]>([]);
  const [newCustomComplaint, setNewCustomComplaint] = useState<string>("");
  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const [validationError, setValidationError] = useState("");
  const [dateQuickSelect, setDateQuickSelect] = useState<
    "today" | "yesterday" | "custom"
  >("today");
  const [customDate, setCustomDate] = useState<string>(
    new Date().toISOString().split("T")[0],
  );
  // CHANGE 1: flight details collapsed by default
  const [flightDetailsOpen, setFlightDetailsOpen] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    name: "",
    email: "",
    phone: "",
    accountNumber: "",
    city: "",
    flightNumber: "",
    frequentFlyerNumber: "",
    departureAirport: "",
    arrivalAirport: "",
    bookingNumber: "",
    loyaltyNumber: "",
    roomType: "",
    nightlyRate: "",
    rentalAgreement: "",
    vehicleModel: "",
    pickupLocation: "",
    disputedAmount: "",
  });
  const [letter, setLetter] = useState("");
  const [hasGeneratedLetter, setHasGeneratedLetter] = useState<boolean>(false);

  const getEffectiveDate = (): Date => {
    if (dateQuickSelect === "yesterday") {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      return d;
    }
    if (dateQuickSelect === "custom" && customDate) return new Date(customDate);
    return new Date();
  };

  const getDateLabel = (): string =>
    getEffectiveDate().toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  const getCompanyType = (cat: string) => {
    switch (cat) {
      case "airline":
        return "AIRLINE";
      case "lodging":
        return "LODGING";
      case "rentalCar":
        return "VEHICLE";
      default:
        return "";
    }
  };

  function sanitizeComplaints(set: Set<string>, delimiter = " | "): string {
    return Array.from(set)
      .map((i) => i.trim())
      .filter((i) => i.length > 0)
      .join(delimiter);
  }

  function formatAsAWSDateTime(date: Date): string {
    return date.toISOString();
  }

  const resetAllStates = () => {
    setStep(1);
    setSnitchId("");
    setSearchTerm("");
    setLoading(false);
    setButtonText("Copy to Clipboard");
    setLetterExpanded(false);
    setLinkCopied(false);
    setShowWin(false);
    setCheckedItems(new Set());
    setCustomItems(new Set());
    setShowModal(false);
    setCategory("airline");
    setCheckboxes([]);
    setNewCustomComplaint("");
    setSelectedCompany(null);
    setCompanies([]);
    setValidationError("");
    setDateQuickSelect("today");
    setCustomDate(new Date().toISOString().split("T")[0]);
    setFlightDetailsOpen(false);
    setContactInfo({
      name: "",
      email: "",
      flightNumber: "",
      frequentFlyerNumber: "",
      departureAirport: "",
      arrivalAirport: "",
      bookingNumber: "",
      loyaltyNumber: "",
      roomType: "",
      nightlyRate: "",
      rentalAgreement: "",
      vehicleModel: "",
      pickupLocation: "",
      disputedAmount: "",
    });
    setLetter("");
    setHasGeneratedLetter(false);
  };

  useEffect(() => {
    const fetchCompaniesAndComplaints = async () => {
      try {
        const companyType = getCompanyType(category);
        const companyResult: any = await API.graphql(
          graphqlOperation(listCompanies, {
            filter: { company_type: { eq: companyType } },
          }),
        );
        setCompanies(
          companyResult.data.listCompanies.items.sort((a: any, b: any) =>
            a.name.localeCompare(b.name),
          ),
        );
        const complaintResult: any = await API.graphql(
          graphqlOperation(listComplaints, {
            filter: { company_type: { eq: companyType } },
          }),
        );
        const complaints = complaintResult.data.listComplaints.items.map(
          (item: any) => item.complaint,
        );
        const priorityOrder: Record<string, string[]> = {
          AIRLINE: [
            "Delayed or Canceled Flight",
            "Bumped Off My Flight",
            "Lost or Damaged Baggage",
            "Poor Customer Service",
            "Uncomfortable Seat",
            "Poor Onboard Facilities",
            "Wrong Ticket or Booking Error",
            "Misled by Airline Staff",
            "Special Request Ignored",
          ],
          LODGING: [
            "Dirty or Unclean Room",
            "Room Not as Described",
            "Charged the Wrong Amount",
            "Issues with Check-In/Check-Out",
            "Something Broken in My Room",
            "Broken or Missing Amenities",
            "Noisy Environment",
            "Uncomfortable Bed or Furniture",
            "Poor Customer Service",
            "Hotel Ignored My Complaint",
          ],
          VEHICLE: [
            "Hidden or Surprise Charges",
            "Charged the Wrong Amount",
            "Car Not as Described",
            "Car Broke Down or Had Problems",
            "Dirty Car at Pickup",
            "Fuel Tank Not Full",
            "Problem at Pickup or Drop-Off",
            "Booking Wasn't Honoured",
            "Poor Customer Service",
            "Rental Company Ignored My Complaint",
          ],
        };
        const sortType = getCompanyType(category);
        const order = priorityOrder[sortType] || [];
        const sorted = [...complaints].sort((a, b) => {
          const ai = order.indexOf(a);
          const bi = order.indexOf(b);
          if (ai === -1 && bi === -1) return 0;
          if (ai === -1) return 1;
          if (bi === -1) return -1;
          return ai - bi;
        });
        setCheckboxes(sorted);
        setCheckedItems(new Set());
        setCustomItems(new Set());
        setSelectedCompany(null);
        setHasGeneratedLetter(false);
        setValidationError("");
        // Reset flight details toggle when category changes
        setFlightDetailsOpen(false);
      } catch (error) {
        console.error("Error fetching companies or complaints:", error);
      }
    };
    fetchCompaniesAndComplaints();
  }, [category]);

  useEffect(() => {
    if (hasGeneratedLetter && !snitchId) setSnitchId(generateSnitchId());
  }, [hasGeneratedLetter, snitchId]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSearchTerm(e.target.value);

  const handleCheckboxChange = (item: string) => {
    setValidationError("");
    setCheckedItems((prev) => {
      const next = new Set(prev);
      if (next.has(item)) next.delete(item);
      else next.add(item);
      return next;
    });
  };

  const handleSelectedCheckboxClick = (item: string) =>
    handleCheckboxChange(item);

  // CHANGE 2: Add custom complaint now lives inside the search row
  const handleAddCustomComplaint = () => {
    const trimmed = newCustomComplaint.trim();
    if (trimmed !== "") {
      setCustomItems((prev) => new Set(prev.add(trimmed)));
      setNewCustomComplaint("");
      setSearchTerm("");
      setValidationError("");
    }
  };

  const handleCustomItemClick = (item: string) => {
    setCustomItems((prev) => {
      const next = new Set(prev);
      next.delete(item);
      return next;
    });
  };

  const handleCompanySelect = (eventKey: string | null) => {
    setSelectedCompany(eventKey !== null ? parseInt(eventKey, 10) : null);
    setValidationError("");
  };

  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setContactInfo((prev) => ({ ...prev, [name]: value }));
  };

  const openEmail = (client: "gmail" | "outlook" | "app") => {
    const email = companies[selectedCompany!]?.email ?? "";
    const subject = encodeURIComponent("Formal Travel Complaint");
    const body = encodeURIComponent(letter);
    if (client === "gmail") {
      window.open(
        `https://mail.google.com/mail/?view=cm&to=${email}&su=${subject}&body=${body}`,
        "_blank",
      );
    } else if (client === "outlook") {
      window.open(
        `https://outlook.live.com/mail/0/deeplink/compose?to=${email}&subject=${subject}&body=${body}`,
        "_blank",
      );
    } else {
      const a = document.createElement("a");
      a.href = `mailto:${email}?subject=${subject}&body=${body}`;
      a.click();
    }
  };

  const handleGenerateComplaint = async () => {
    try {
      setLoading(true);
      const effectiveDate = getEffectiveDate();
      const allComplaints = new Set([...checkedItems, ...customItems]);

      let prompt = `Generate a complaint email for the following:\nCompany: ${companies[selectedCompany!].name}\nComplaints: ${Array.from(checkedItems).join(", ")}\nCustom Complaints: ${Array.from(customItems).join(", ")}\nDate Occurred: ${effectiveDate.toDateString()}`;
      if (contactInfo.name) prompt += `\nName: ${contactInfo.name}`;
      if (contactInfo.email) prompt += `\nEmail: ${contactInfo.email}`;
      if (category === "airline") {
        if (contactInfo.flightNumber)
          prompt += `\nFlight Number: ${contactInfo.flightNumber}`;
        if (contactInfo.frequentFlyerNumber)
          prompt += `\nFrequent Flyer Number: ${contactInfo.frequentFlyerNumber}`;
        if (contactInfo.departureAirport)
          prompt += `\nDeparture Airport: ${contactInfo.departureAirport}`;
        if (contactInfo.arrivalAirport)
          prompt += `\nArrival Airport: ${contactInfo.arrivalAirport}`;
        prompt += `\nNote: Where applicable, reference relevant passenger rights regulations such as EC 261/2004 (EU) or DOT regulations (US) and request appropriate compensation.`;
      } else if (category === "lodging") {
        if (contactInfo.bookingNumber)
          prompt += `\nBooking/Confirmation Number: ${contactInfo.bookingNumber}`;
        if (contactInfo.loyaltyNumber)
          prompt += `\nLoyalty Membership Number: ${contactInfo.loyaltyNumber}`;
        if (contactInfo.roomType)
          prompt += `\nRoom Type Booked: ${contactInfo.roomType}`;
        if (contactInfo.nightlyRate)
          prompt += `\nNightly Rate Paid: $${contactInfo.nightlyRate}`;
        prompt += `\nNote: Reference consumer protection law for misrepresented bookings and request a refund proportional to the issues experienced.`;
      } else if (category === "rentalCar") {
        if (contactInfo.rentalAgreement)
          prompt += `\nRental Agreement Number: ${contactInfo.rentalAgreement}`;
        if (contactInfo.vehicleModel)
          prompt += `\nVehicle Make & Model: ${contactInfo.vehicleModel}`;
        if (contactInfo.pickupLocation)
          prompt += `\nPickup Location: ${contactInfo.pickupLocation}`;
        if (contactInfo.disputedAmount)
          prompt += `\nDisputed Charge Amount: $${contactInfo.disputedAmount}`;
        prompt += `\nNote: Reference the consumer's right to request a written damage assessment report and cite consumer protection law regarding unfair charges.`;
      }
      prompt +=
        "\nDo not include any letter head or footer, except the sign-off signature. Do not include a subject line. For the sign-off, use only the name explicitly provided above — do not infer or derive any additional name from the email address.";

      let generatedText: string | null = null;
      try {
        const response: any = await API.graphql(
          graphqlOperation(generateOpenAICompletion, {
            prompt,
            recipientEmail: contactInfo.email || undefined,
            recipientName: contactInfo.name || undefined,
          }),
        );
        generatedText =
          response.data?.generateOpenAICompletion?.text ||
          response.data?.generateOpenAICompletion?.error ||
          null;
      } catch (genErr: any) {
        console.error("OpenAI generation failed:", genErr);
        generatedText = genErr?.errors?.[0]?.message || null;
      }

      const submissionInput = {
        submissionId: uuidv4(),
        company_id: companies[selectedCompany!].company_id,
        complaints: sanitizeComplaints(allComplaints),
        name: contactInfo.name || "",
        email: contactInfo.email || "",
        phone: contactInfo.phone || "",
        dateOccurred: formatAsAWSDateTime(effectiveDate),
        generatedEmail: generatedText,
        timestamp: Math.floor(Date.now() / 1000),
        userId: user?.sub || null,
      };
      const submissionResult: any = await API.graphql(
        graphqlOperation(createSubmissions, { input: submissionInput }),
      );
      if (submissionResult.errors) {
        console.error("GraphQL errors:", submissionResult.errors);
      }

      setLetter(generatedText || "Generation failed. Please try again.");
      setButtonText("Copy to Clipboard");
      setHasGeneratedLetter(true);
      setShowWin(false);
      setStep(2);
    } catch (err: any) {
      console.error(
        "handleGenerateComplaint error:",
        JSON.stringify(err, null, 2),
      );
      setValidationError(
        err?.errors?.[0]?.message ||
          err?.message ||
          "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    if (selectedCompany === null) {
      setValidationError("Please select a company to continue.");
      return;
    }
    if (checkedItems.size === 0 && customItems.size === 0) {
      setValidationError("Please select at least one complaint to continue.");
      return;
    }
    if (!contactInfo.name.trim()) {
      setValidationError("Please enter your full name.");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (
      !contactInfo.email.trim() ||
      !emailRegex.test(contactInfo.email.trim())
    ) {
      setValidationError(
        "Please enter a valid email address (e.g. you@example.com).",
      );
      return;
    }
    setValidationError("");
    await handleGenerateComplaint();
  };

  const handleCopyEmail = (email: string) => {
    navigator.clipboard.writeText(email).catch(console.error);
  };

  // CHANGE 1: category-specific fields wrapped in collapsible toggle
  const renderCategoryFields = () => {
    const hintStyle: React.CSSProperties = {
      fontSize: "0.8rem",
      color: "#1e40af",
      background: "#eff6ff",
      borderRadius: "6px",
      padding: "8px 10px",
      marginBottom: "12px",
      border: "1px solid #bfdbfe",
    };

    const categoryLabel =
      category === "airline"
        ? "Flight details"
        : category === "lodging"
          ? "Stay details"
          : "Rental details";

    const categoryHint =
      category === "airline"
        ? "✈ Adding flight details lets us reference EC 261/2004 (EU) or DOT rights automatically — stronger complaint."
        : category === "lodging"
          ? "🏨 Hotels are liable for misrepresented bookings — your nightly rate helps us quantify the refund request."
          : "🚗 Rental companies must provide written damage assessments — our complaint will cite this right and request proof.";

    return (
      <>
        {/* Toggle header */}
        <div
          onClick={() => setFlightDetailsOpen((prev) => !prev)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
            userSelect: "none",
            marginBottom: flightDetailsOpen ? "12px" : "0",
          }}
        >
          <span style={{ color: "#1a73e8", fontSize: "0.85rem" }}>
            {flightDetailsOpen ? <FaChevronDown /> : <FaChevronRight />}
          </span>
          <span style={{ fontWeight: 600, fontSize: "0.9rem", color: "#1a73e8" }}>
            {categoryLabel}
          </span>
          <span
            style={{
              fontSize: "0.7rem",
              fontWeight: 500,
              background: "#dbeafe",
              color: "#1e40af",
              borderRadius: "4px",
              padding: "2px 7px",
            }}
          >
            Makes your complaint stronger
          </span>
        </div>

        {/* Collapsible fields */}
        {flightDetailsOpen && (
          <>
            <p style={hintStyle}>{categoryHint}</p>
            {category === "airline" && (
              <Row>
                <Col xs={12} md={6}>
                  <Form.Control
                    type="text"
                    placeholder="Flight number"
                    name="flightNumber"
                    value={contactInfo.flightNumber}
                    onChange={handleContactChange}
                    className="mb-2"
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Form.Control
                    type="text"
                    placeholder="Frequent flyer # (optional)"
                    name="frequentFlyerNumber"
                    value={contactInfo.frequentFlyerNumber}
                    onChange={handleContactChange}
                    className="mb-2"
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Form.Control
                    type="text"
                    placeholder="Departure airport"
                    name="departureAirport"
                    value={contactInfo.departureAirport}
                    onChange={handleContactChange}
                    className="mb-2"
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Form.Control
                    type="text"
                    placeholder="Arrival airport"
                    name="arrivalAirport"
                    value={contactInfo.arrivalAirport}
                    onChange={handleContactChange}
                    className="mb-2"
                  />
                </Col>
              </Row>
            )}
            {category === "lodging" && (
              <Row>
                <Col xs={12} md={6}>
                  <Form.Control
                    type="text"
                    placeholder="Booking / confirmation #"
                    name="bookingNumber"
                    value={contactInfo.bookingNumber}
                    onChange={handleContactChange}
                    className="mb-2"
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Form.Control
                    type="text"
                    placeholder="Loyalty membership # (optional)"
                    name="loyaltyNumber"
                    value={contactInfo.loyaltyNumber}
                    onChange={handleContactChange}
                    className="mb-2"
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Form.Control
                    type="text"
                    placeholder="Room type booked"
                    name="roomType"
                    value={contactInfo.roomType}
                    onChange={handleContactChange}
                    className="mb-2"
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Form.Control
                    type="text"
                    placeholder="Nightly rate paid ($)"
                    name="nightlyRate"
                    value={contactInfo.nightlyRate}
                    onChange={handleContactChange}
                    className="mb-2"
                  />
                </Col>
              </Row>
            )}
            {category === "rentalCar" && (
              <Row>
                <Col xs={12} md={6}>
                  <Form.Control
                    type="text"
                    placeholder="Rental agreement / booking #"
                    name="rentalAgreement"
                    value={contactInfo.rentalAgreement}
                    onChange={handleContactChange}
                    className="mb-2"
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Form.Control
                    type="text"
                    placeholder="Vehicle make & model"
                    name="vehicleModel"
                    value={contactInfo.vehicleModel}
                    onChange={handleContactChange}
                    className="mb-2"
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Form.Control
                    type="text"
                    placeholder="Pickup location"
                    name="pickupLocation"
                    value={contactInfo.pickupLocation}
                    onChange={handleContactChange}
                    className="mb-2"
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Form.Control
                    type="text"
                    placeholder="Disputed charge amount ($)"
                    name="disputedAmount"
                    value={contactInfo.disputedAmount}
                    onChange={handleContactChange}
                    className="mb-2"
                  />
                </Col>
              </Row>
            )}
          </>
        )}
      </>
    );
  };

  const filteredCheckboxes = checkboxes.filter((cb) =>
    cb.toLowerCase().includes(searchTerm.toLowerCase()),
  );
  const topComplaints: Record<string, string[]> = {
    AIRLINE: [
      "Delayed or Canceled Flight",
      "Bumped Off My Flight",
      "Lost or Damaged Baggage",
    ],
    LODGING: [
      "Dirty or Unclean Room",
      "Charged the Wrong Amount",
      "Issues with Check-In/Check-Out",
    ],
    VEHICLE: [
      "Hidden or Surprise Charges",
      "Car Not as Described",
      "Car Broke Down or Had Problems",
    ],
  };
  const top = topComplaints[getCompanyType(category)] || [];
  const initialCheckboxes = searchTerm
    ? filteredCheckboxes
    : checkboxes.filter((cb) => top.includes(cb));

  // Whether the search term matches no existing complaints (show "Add" inline)
  const noMatchFound =
    searchTerm.trim().length > 0 && filteredCheckboxes.length === 0;

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <>
            <Row>
              <Col xs={12}>
                <p
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "#888",
                    marginBottom: "2px",
                  }}
                >
                  One step · Under 60 seconds
                </p>
                <h4>Tell Us What Happened</h4>
                <p className="text-muted">
                  Pick the company, your issue, and your details — we'll handle
                  the rest.
                </p>
              </Col>
            </Row>

            <Row className="mt-2">
              <Col xs={12}>
                <h5 className="mb-2">
                  Company <span style={{ color: "red" }}>*</span>
                </h5>
                <ButtonGroup className="mb-3 w-100">
                  <Button
                    variant="outline-primary"
                    active={category === "airline"}
                    onClick={() => setCategory("airline")}
                    style={{ flex: 1 }}
                  >
                    <FaPlane className="me-2" />
                    Airline
                  </Button>
                  <Button
                    variant="outline-primary"
                    active={category === "lodging"}
                    onClick={() => setCategory("lodging")}
                    style={{ flex: 1 }}
                  >
                    <FaHotel className="me-2" />
                    Lodging
                  </Button>
                  <Button
                    variant="outline-primary"
                    active={category === "rentalCar"}
                    onClick={() => setCategory("rentalCar")}
                    style={{ flex: 1 }}
                  >
                    <FaCar className="me-2" />
                    Rental Car
                  </Button>
                </ButtonGroup>
                <Dropdown onSelect={handleCompanySelect}>
                  <Dropdown.Toggle variant="primary" id="dropdown-company">
                    {selectedCompany !== null && companies[selectedCompany]
                      ? companies[selectedCompany].name
                      : category === "airline"
                        ? "Select airline..."
                        : category === "lodging"
                          ? "Select hotel..."
                          : "Select rental company..."}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {companies.map((company, index) => (
                      <Dropdown.Item key={company.company_id} eventKey={index}>
                        {company.name}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
            </Row>

            <Row className="mt-4">
              <Col xs={12}>
                <h5 className="mb-2">
                  What went wrong? <span style={{ color: "red" }}>*</span>
                </h5>

                {/* Selected items as pill badges */}
                {(checkedItems.size > 0 || customItems.size > 0) && (
                  <div className="mb-2">
                    {Array.from(checkedItems).map((item, i) => (
                      <Button
                        key={i}
                        variant="secondary"
                        size="sm"
                        className="me-2 mb-2"
                        onClick={() => handleSelectedCheckboxClick(item)}
                      >
                        ✓ {item}
                      </Button>
                    ))}
                    {Array.from(customItems).map((item, i) => (
                      <Button
                        key={i}
                        variant="warning"
                        size="sm"
                        className="me-2 mb-2"
                        onClick={() => handleCustomItemClick(item)}
                      >
                        {item} ×
                      </Button>
                    ))}
                  </div>
                )}

                {initialCheckboxes.map((cb, i) => (
                  <Form.Check
                    type="checkbox"
                    key={i}
                    label={cb}
                    checked={checkedItems.has(cb)}
                    onChange={() => handleCheckboxChange(cb)}
                    className="mb-2"
                  />
                ))}

                {/* CHANGE 2: Unified search + add — single input, "Add" appears when no match */}
                <InputGroup className="mt-2 mb-1">
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Other issue? Search or type your own..."
                    style={{ fontSize: "0.85rem" }}
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setNewCustomComplaint(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && noMatchFound)
                        handleAddCustomComplaint();
                    }}
                  />
                  {noMatchFound && (
                    <Button variant="success" onClick={handleAddCustomComplaint}>
                      Add
                    </Button>
                  )}
                </InputGroup>
                {noMatchFound && (
                  <p style={{ fontSize: "0.78rem", color: "#6c757d", marginBottom: 0 }}>
                    No match found — press Add or hit Enter to add as a custom complaint.
                  </p>
                )}
              </Col>
            </Row>

            <hr className="my-4" />

            <Row>
              <Col xs={12}>
                <h5 className="mb-3">
                  Your details <span style={{ color: "red" }}>*</span>
                </h5>
                <Row>
                  <Col xs={12} md={6}>
                    <Form.Control
                      type="text"
                      placeholder="Full name"
                      name="name"
                      value={contactInfo.name}
                      onChange={handleContactChange}
                      className="mb-2"
                    />
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Control
                      type="email"
                      placeholder="Email address"
                      name="email"
                      value={contactInfo.email}
                      onChange={handleContactChange}
                      className="mb-2"
                    />
                  </Col>
                </Row>
                <p
                  style={{
                    fontSize: "0.8rem",
                    color: "#6c757d",
                    marginBottom: "0",
                  }}
                >
                  🔒 Your info is never shared. Used only to personalise your
                  complaint.
                </p>
              </Col>
            </Row>

            {/* CHANGE 1: flight/stay/rental details collapsed by default */}
            <Row className="mt-4">
              <Col xs={12}>
                {renderCategoryFields()}
              </Col>
            </Row>

            <Row className="mt-2">
              <Col xs={12}>
                <h6 className="mb-2">When did it happen?</h6>
                <div className="d-flex gap-2 flex-wrap mb-2">
                  <Button
                    variant={
                      dateQuickSelect === "today"
                        ? "primary"
                        : "outline-secondary"
                    }
                    size="sm"
                    onClick={() => setDateQuickSelect("today")}
                  >
                    Today
                  </Button>
                  <Button
                    variant={
                      dateQuickSelect === "yesterday"
                        ? "primary"
                        : "outline-secondary"
                    }
                    size="sm"
                    onClick={() => setDateQuickSelect("yesterday")}
                  >
                    Yesterday
                  </Button>
                  <Button
                    variant={
                      dateQuickSelect === "custom"
                        ? "primary"
                        : "outline-secondary"
                    }
                    size="sm"
                    onClick={() => setDateQuickSelect("custom")}
                  >
                    Pick a date...
                  </Button>
                </div>
                {dateQuickSelect === "custom" ? (
                  <Form.Control
                    type="date"
                    value={customDate}
                    max={new Date().toISOString().split("T")[0]}
                    onChange={(e) => setCustomDate(e.target.value)}
                    style={{ maxWidth: "200px" }}
                  />
                ) : (
                  <p
                    style={{
                      fontSize: "0.82rem",
                      color: "#2563eb",
                      marginBottom: 0,
                    }}
                  >
                    Using: {getDateLabel()}
                  </p>
                )}
              </Col>
            </Row>

            {validationError && (
              <div
                className="mt-3"
                style={{
                  color: "#9b1c1c",
                  fontSize: "0.85rem",
                  padding: "8px 12px",
                  background: "#fff5f5",
                  borderRadius: "6px",
                  border: "1px solid #fca5a5",
                }}
              >
                ⚠️ {validationError}
              </div>
            )}
          </>
        );

      case 2:
        const companyName =
          selectedCompany !== null
            ? companies[selectedCompany]?.name || "the company"
            : "the company";

        const filedTweet = encodeURIComponent(
          `Just filed a complaint against ${companyName} using @TravelSnitch — took 60 seconds. Public pressure works 👀 travelsnitch.net`,
        );
        const winTweet = encodeURIComponent(
          `Just got a response from ${companyName} after filing through @TravelSnitch. Took less than 2 mins to file. Try it free → travelsnitch.net ✈️`,
        );

        return (
          <>
            {/* Completion banner */}
            <div
              style={{
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: "10px",
                padding: "14px 16px",
                marginBottom: "20px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <span style={{ fontSize: "1.4rem" }}>✅</span>
              <div>
                <p
                  style={{
                    fontWeight: 700,
                    color: "#166534",
                    marginBottom: "2px",
                    fontSize: "0.95rem",
                  }}
                >
                  Your complaint is ready to send
                </p>
                <p
                  style={{
                    fontSize: "0.82rem",
                    color: "#166534",
                    marginBottom: 0,
                    opacity: 0.8,
                  }}
                >
                  {selectedCompany !== null
                    ? `Filed against ${companies[selectedCompany]?.name}`
                    : ""}
                  {" · "}
                  {new Date().toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </p>
              </div>
            </div>

            {hasGeneratedLetter && (
              <>
                {/* ── CHANGE 3: LETTER FIRST — moment of delight before any asks ── */}
                <h4 className="mb-3">Review Your Complaint Email</h4>
                <Row>
                  <Col xs={12} md={10} className="mx-auto">
                    <Form.Control
                      as="textarea"
                      rows={letterExpanded ? undefined : 10}
                      value={letter}
                      onChange={(e) => setLetter(e.target.value)}
                      style={letterExpanded ? { minHeight: "600px" } : {}}
                    />
                    <Button
                      variant="link"
                      size="sm"
                      className="mt-1 p-0"
                      onClick={() => setLetterExpanded((prev) => !prev)}
                      style={{
                        fontSize: "0.82rem",
                        color: "#2563eb",
                        textDecoration: "none",
                      }}
                    >
                      {letterExpanded ? "▲ Collapse" : "▼ Show full letter"}
                    </Button>
                  </Col>
                </Row>

                {/* ── SEND BUTTONS — after the letter ── */}
                <Row className="mt-4">
                  <Col xs={12} md={10} className="mx-auto">
                    <p
                      style={{
                        fontWeight: 700,
                        fontSize: "0.88rem",
                        color: "#111",
                        marginBottom: "8px",
                      }}
                    >
                      Step 2 — Send it now →
                    </p>
                    {/iPhone|iPad|Android/i.test(navigator.userAgent) ? (
                      <Button
                        variant="primary"
                        className="w-100"
                        style={{
                          padding: "12px",
                          fontSize: "1.05rem",
                          fontWeight: "700",
                        }}
                        onClick={() => openEmail("app")}
                      >
                        📤 Open in Email App
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="primary"
                          className="w-100"
                          style={{
                            padding: "12px",
                            fontSize: "1.05rem",
                            fontWeight: "700",
                          }}
                          onClick={() => openEmail("gmail")}
                        >
                          📤 Send via Gmail
                        </Button>
                        <Button
                          variant="outline-secondary"
                          className="w-100 mt-2"
                          onClick={() => openEmail("outlook")}
                        >
                          📤 Send via Outlook
                        </Button>
                      </>
                    )}

                    {/* Company email row */}
                    {selectedCompany !== null && (
                      <div
                        className="mt-2"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          backgroundColor: "white",
                          border: "1px solid #ccc",
                          borderRadius: "5px",
                          padding: "8px 12px",
                        }}
                      >
                        <BiEnvelope
                          size={18}
                          style={{ marginRight: "8px", flexShrink: 0 }}
                        />
                        <Form.Control
                          type="text"
                          value={companies[selectedCompany].email}
                          readOnly
                          style={{
                            border: "none",
                            backgroundColor: "white",
                            cursor: "default",
                            padding: "0",
                            flex: 1,
                          }}
                        />
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() =>
                            handleCopyEmail(companies[selectedCompany].email)
                          }
                          style={{
                            marginLeft: "10px",
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                          }}
                        >
                          <BiCopy size={22} />
                        </Button>
                      </div>
                    )}
                  </Col>
                </Row>

                {/* What happens next */}
                <Row className="mt-3">
                  <Col xs={12} md={10} className="mx-auto">
                    <div
                      style={{
                        background: "#f0fdf4",
                        border: "1px solid #bbf7d0",
                        borderRadius: "8px",
                        padding: "12px 14px",
                        fontSize: "0.85rem",
                        color: "#166534",
                      }}
                    >
                      📬 <strong>What happens next?</strong> Check your inbox
                      for a reply from{" "}
                      {selectedCompany !== null
                        ? companies[selectedCompany]?.name
                        : "the company"}
                      .{" "}
                      {category === "airline"
                        ? "Airlines typically respond within 5–14 days."
                        : category === "lodging"
                          ? "Hotels typically respond within 3–7 days."
                          : "Rental companies typically respond within 5–10 days."}
                    </div>
                  </Col>
                </Row>

                {/* ── SHARE — tweet only, no "already got a response" peer ── */}
                <Row className="mt-3">
                  <Col xs={12} md={10} className="mx-auto">
                    <div
                      style={{
                        background: showWin ? "#f0fdf4" : "#f8f7ff",
                        border: `1.5px solid ${showWin ? "#bbf7d0" : "#ede8ff"}`,
                        borderRadius: "12px",
                        padding: "20px 24px",
                        textAlign: "center",
                        transition: "background 0.25s, border-color 0.25s",
                      }}
                    >
                      {!showWin ? (
                        <>
                          <p
                            style={{
                              fontWeight: 700,
                              fontSize: "0.95rem",
                              marginBottom: "6px",
                              color: "#111",
                            }}
                          >
                            📣 Make some noise — public complaints get faster
                            responses.
                          </p>
                          <p
                            style={{
                              fontSize: "0.82rem",
                              color: "#666",
                              marginBottom: "16px",
                            }}
                          >
                            Tweet that you filed. Companies monitor mentions. It
                            works.
                          </p>
                          <a
                            href={`https://x.com/intent/tweet?text=${filedTweet}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              background: "#000",
                              color: "#fff",
                              borderRadius: "100px",
                              padding: "10px 20px",
                              fontWeight: 700,
                              fontSize: "0.85rem",
                              textDecoration: "none",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "7px",
                            }}
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="white">
                              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                            </svg>
                            Tweet it
                          </a>
                        </>
                      ) : (
                        <>
                          <p style={{ fontSize: "1.6rem", marginBottom: "6px" }}>🏆</p>
                          <p
                            style={{
                              fontWeight: 800,
                              fontSize: "1.05rem",
                              color: "#15803d",
                              marginBottom: "6px",
                            }}
                          >
                            That's what we're talking about.
                          </p>
                          <p
                            style={{
                              fontSize: "0.85rem",
                              color: "#555",
                              marginBottom: "16px",
                            }}
                          >
                            Share your win — one tweet from you is worth more
                            than anything we can post ourselves.
                          </p>
                          <a
                            href={`https://x.com/intent/tweet?text=${winTweet}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              background: "#000",
                              color: "#fff",
                              borderRadius: "100px",
                              padding: "11px 28px",
                              fontWeight: 700,
                              fontSize: "0.9rem",
                              textDecoration: "none",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                            </svg>
                            Share my win on X
                          </a>
                        </>
                      )}
                    </div>
                  </Col>
                </Row>

                {/* CHANGE 4: "Already got a response?" — own section, below tweet box */}
                {!showWin && (
                  <Row className="mt-3">
                    <Col xs={12} md={10} className="mx-auto">
                      <div
                        style={{
                          border: "1px solid #bbf7d0",
                          borderRadius: "10px",
                          padding: "14px 18px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: "10px",
                        }}
                      >
                        <div>
                          <p style={{ fontWeight: 700, fontSize: "0.88rem", marginBottom: "2px", color: "#111" }}>
                            Did it work?
                          </p>
                          <p style={{ fontSize: "0.78rem", color: "#6c757d", marginBottom: 0 }}>
                            Let us know if you already got a response or compensation.
                          </p>
                        </div>
                        <button
                          onClick={() => setShowWin(true)}
                          style={{
                            background: "#22c55e",
                            color: "#fff",
                            border: "none",
                            borderRadius: "100px",
                            padding: "9px 18px",
                            fontWeight: 700,
                            fontSize: "0.85rem",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                          }}
                        >
                          🎉 I already got a response!
                        </button>
                      </div>
                    </Col>
                  </Row>
                )}

                {/* CHANGE 4: Refer a Friend — outcome as headline */}
                <Row className="mt-4 mb-5">
                  <Col xs={12} md={10} className="mx-auto">
                    <div
                      style={{
                        background: "#f9f5ff",
                        border: "1px solid #e9d5ff",
                        borderRadius: "12px",
                        padding: "18px 20px",
                      }}
                    >
                      <h5 style={{ color: "#6b21a8", marginBottom: "4px" }}>
                        Your friend could get $200+ back too.
                      </h5>
                      <p style={{ fontSize: "0.85rem", color: "#7c3aed", marginBottom: "14px", opacity: 0.85 }}>
                        Share Travel Snitch — it's free and takes 60 seconds.
                      </p>
                      <div className="d-flex gap-3">
                        {/* Copy link */}
                        <div style={{ position: "relative", display: "inline-block" }}>
                          <button
                            title="Copy link"
                            onClick={() => {
                              navigator.clipboard.writeText("https://travelsnitch.net");
                              setLinkCopied(true);
                              setTimeout(() => setLinkCopied(false), 2000);
                            }}
                            style={{
                              background: "#f0f0f0",
                              border: "none",
                              borderRadius: "50%",
                              width: "48px",
                              height: "48px",
                              fontSize: "20px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            🔗
                          </button>
                          {linkCopied && (
                            <div
                              style={{
                                position: "absolute",
                                bottom: "54px",
                                left: "50%",
                                transform: "translateX(-50%)",
                                background: "#111",
                                color: "#fff",
                                fontSize: "11px",
                                padding: "4px 8px",
                                borderRadius: "4px",
                                whiteSpace: "nowrap",
                                zIndex: 10,
                              }}
                            >
                              Copied!
                            </div>
                          )}
                        </div>
                        {/* iMessage / SMS */}
                        <button
                          title="Share via iMessage / SMS"
                          onClick={() => {
                            const msg = encodeURIComponent(
                              "Just used Travel Snitch to file a complaint — took 60 seconds. Try it: https://travelsnitch.net",
                            );
                            window.open(`sms:?body=${msg}`, "_self");
                          }}
                          style={{
                            background: "#34c759",
                            border: "none",
                            borderRadius: "50%",
                            width: "48px",
                            height: "48px",
                            fontSize: "20px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          💬
                        </button>
                        {/* WhatsApp */}
                        <button
                          title="Share on WhatsApp"
                          onClick={() => {
                            const msg = encodeURIComponent(
                              "Just used Travel Snitch to file a complaint — took 60 seconds. Try it: https://travelsnitch.net",
                            );
                            window.open(`https://wa.me/?text=${msg}`, "_blank");
                          }}
                          style={{
                            background: "#25d366",
                            border: "none",
                            borderRadius: "50%",
                            width: "48px",
                            height: "48px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="white">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </Col>
                </Row>
              </>
            )}
          </>
        );
    }
  };

  return (
    <Container fluid className="mt-4 px-3">
      <Header onReset={resetAllStates} />
      <div
        className="step-wrapper mx-auto mt-4 p-4 d-flex flex-column justify-content-between"
        style={{
          position: "relative",
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          borderRadius: "12px",
          maxWidth: "600px",
          width: "100%",
          minHeight: "auto",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}
      >
        <div>{renderStep()}</div>
        <Row className="mt-auto">
          <Col className="d-flex justify-content-end mt-3">
            {step === 2 && (
              <Button
                variant="secondary"
                onClick={() => setStep(1)}
                disabled={loading}
                className="me-2"
              >
                Back
              </Button>
            )}
            {step === 1 && (
              <Button
                variant="primary"
                onClick={handleNext}
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "12px",
                  fontSize: "1.1rem",
                  fontWeight: "700",
                }}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    />
                    Generating...
                  </>
                ) : (
                  "Generate My Complaint →"
                )}
              </Button>
            )}
            {step === 2 && (
              <Button
                variant="primary"
                onClick={handleGenerateComplaint}
                disabled={loading || !hasGeneratedLetter}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    />
                    Regenerating...
                  </>
                ) : (
                  "Regenerate Email"
                )}
              </Button>
            )}
          </Col>
        </Row>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Select Complaints</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ListGroup>
            {checkboxes.map((checkbox, index) => (
              <ListGroup.Item key={index}>
                <Form.Check
                  type="checkbox"
                  label={checkbox}
                  checked={checkedItems.has(checkbox)}
                  onChange={() => handleCheckboxChange(checkbox)}
                />
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default TravelSnitchScreen;
